/**
 * How wide a column is.
 *
 * **No column is ever narrower than what it holds** — the larger of its heading and its longest
 * cell. That floor is why nothing has to be dropped as the table narrows, and why a heading is
 * worth writing even on a column whose cells are wider than it.
 *
 * `100%` is the exception and the one column that gives way: when room is short it is the one that
 * shrinks below its content, so it is where an ellipsis and a wrap happen. One per table.
 *
 * **Room to spare is shared** between the columns that accept it, as the columns of a full-width
 * `<table>` share it: each takes its content and then an equal part of what is left. `auto` is that,
 * and it is the default.
 *
 * A column that should take none says so with `fit-content`, which is exactly as wide as what it
 * holds — that is what a column of controls wants, so its button stays on the edge instead of
 * walking away from it as the table widens. Writing a length pins the column too, but a length is a
 * measurement that stops being true the day the content changes; `fit-content` is the intent.
 */
export type MibTableSize = '100%' | 'auto' | 'fit-content' | `${number}%` | `${number}rem` | `${number}px`;

/**
 * The height of the heading line, on the control scale of Foundations / Shape and spacing.
 *
 * It is the scale and not a free length because a heading sits in a row of controls — a filter, a
 * search, a toggle — and reads as one of them.
 */
export type MibTableHeaderSize = 'sm' | 'md' | 'lg' | 'xl';

/** 32 · 36 · 40 · 44, the four control heights, with nothing in between. */
export const MIB_TABLE_HEADER_HEIGHT: Record<MibTableHeaderSize, string> = {
  sm: 'min-h-8',
  md: 'min-h-9',
  lg: 'min-h-10',
  xl: 'min-h-11',
};

/**
 * What the column that gives way does with a content wider than it: cut it with an ellipsis, or
 * wrap it and let the row grow.
 *
 * Named `wrap` and not `line-break`, which is a CSS property of its own about where a line may be
 * broken in Chinese, Japanese and Korean — a different question with a confusable name.
 */
export type MibTableOverflow = 'ellipsis' | 'wrap';

/**
 * Where the content of a column sits across it — its heading included, so the column reads as one.
 *
 * The same three words `<mib-menu>` uses, and they follow the direction of the text rather than the
 * screen: `start` is the left in a left-to-right page.
 */
export type MibTableAlign = 'start' | 'center' | 'end';

/**
 * What the column holds. It decides the default {@link MibTableColumn.align} — a column of figures
 * is ranged `end` so the digits line up down it — and `number` also sets them on tabular digits.
 */
export type MibTableType = 'text' | 'number';

/** What every column carries, whether it gives way or holds its content. */
interface MibTableColumnBase {
  /** The identity of the column, and the field of the row it shows when no template is written. */
  key: string;
  /**
   * The word above the column. Left out, the heading is blank — for a column of controls.
   *
   * It also holds the column open: a column is never narrower than its heading, so a short field
   * under a long word keeps the room the word needs.
   */
  label?: string;
  /** Defaults to `text`. */
  type?: MibTableType;
  /** Defaults to `start`, or to `end` for a `number` column. Applies to the heading too. */
  align?: MibTableAlign;
}

/**
 * The one column that gives way: when room is short it is the only one ever narrower than what it
 * holds — so it is the only one that can be asked what to do about it.
 */
export interface MibTableGivingColumn extends MibTableColumnBase {
  size: '100%';
  /** Defaults to `ellipsis`. */
  overflow?: MibTableOverflow;
}

/**
 * A column that holds its content: it is never squeezed below the larger of its heading and its
 * longest cell, so it never has anything to cut and takes no {@link MibTableOverflow}.
 */
export interface MibTableHoldingColumn extends MibTableColumnBase {
  /** Defaults to `auto` — its content plus an equal share of the room to spare. */
  size?: Exclude<MibTableSize, '100%'>;
  /** A column that is never narrower than its content has nothing to cut and nothing to wrap. */
  overflow?: never;
}

/**
 * One column of a `<mib-table>`, declared as data so the list of them can live where the page keeps
 * its configuration and grow without a template changing.
 *
 * ```ts
 * const COLUMNS: MibTableColumn[] = [
 *   { key: 'title', label: 'Case', size: '100%', overflow: 'wrap' },
 *   { key: 'operator', label: 'Operator' },
 *   { key: 'length', label: 'Length', type: 'number' },
 *   { key: 'actions', align: 'end' },
 * ];
 * ```
 *
 * Without a template the cell shows `row[key]` as text. With an `<ng-template mibCell="key">` the
 * page writes the cell itself — a picture, a badge, a menu — and `key` is what ties the two.
 */
export type MibTableColumn = MibTableGivingColumn | MibTableHoldingColumn;

/**
 * What makes **the whole row** a link to the thing it stands for.
 *
 * The table draws one anchor the size of the row, so a ctrl-click, a middle click or "Open in new
 * tab" open the detail the way they do anywhere else, and the browser shows the address on hover —
 * none of which a click handler on a row could give. The page writes no anchor of its own.
 *
 * `link` and `label` are **paths in a row**, written as they would be read — `row.href`, and
 * `row.author.name` for a field one level down. `target` is the only value written as itself.
 *
 * ```html
 * <mib-table [rowLink]="{ link: 'row.href', label: 'row.title' }" (rowOpened)="open($event)" …>
 * ```
 */
export interface MibTableRowLink {
  /** Where the address is in a row — `row.href`. A row whose field is empty is not a link. */
  link: string;
  /**
   * Where the name of the row is — `row.title`. Defaults to the field of the first column.
   *
   * The anchor covers the row and holds no text of its own, so without a name a screen reader
   * announces the row as a blank link.
   */
  label?: string;
  /** Written on the anchor as it would be on any link — `_blank` to open in a new tab every time. */
  target?: '_self' | '_blank';
}

/**
 * What a row hands over when it is opened in place.
 *
 * The event comes with the row because the table's anchor is a real link: a page that routes in
 * place calls `preventDefault()` on it, and one that lets the browser follow the address does not.
 */
export interface MibTableRowOpened<T> {
  row: T;
  event: MouseEvent;
}

/**
 * The band drawn above a group of rows, returned by `rowSection` for each row. A band is drawn
 * whenever `label` differs from the previous row's; the table never sorts nor groups the rows.
 */
export interface MibTableSection {
  /** The title of the band, and the rows sharing it form the group. */
  readonly label: string;
  /** A line of metadata after the title. */
  readonly description?: string;
}

/** Both the cell's own text and where its children sit, so a picture centres as a word does. */
export const MIB_TABLE_ALIGN: Record<MibTableAlign, string> = {
  start: 'justify-start text-left',
  center: 'justify-center text-center',
  end: 'justify-end text-right',
};
